/**
 * HMRC VAT check (F8 P2): a `vat` field's UK number is checked with HMRC's
 * "Check a UK VAT number" API — on the form as the applicant types, and again
 * by the Worker on submit. An unknown UK number can't be sent; a number HMRC
 * can't check (overseas) or an HMRC outage is accepted and flagged to staff.
 */

/** A typed VAT number, sorted into what HMRC can check. */
export type VatNumber =
  | { kind: 'uk'; vrn: string; display: string }
  | { kind: 'other'; display: string };

/**
 * Sort a typed VAT number: spaces, dots and dashes dropped, upper-cased. A UK
 * number is 9 or 12 digits, optionally after GB or XI (Northern Ireland).
 * Anything else that's answered is `other` (e.g. an EU number). Returns null
 * for an empty answer.
 */
export function parseVatNumber(raw: unknown): VatNumber | null {
  if (typeof raw !== 'string') return null;
  const s = raw.replace(/[\s.-]+/g, '').toUpperCase();
  if (!s) return null;
  const m = /^(GB|XI)?(\d{9}|\d{12})$/.exec(s);
  if (m) return { kind: 'uk', vrn: m[2]!, display: `${m[1] ?? 'GB'}${m[2]}` };
  return { kind: 'other', display: s };
}

/** A UK number still being typed: GB/XI and digits, but not 9 or 12 yet. */
export function isPartialUkVat(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  const s = raw.replace(/[\s.-]+/g, '').toUpperCase();
  return /^(GB|XI)?\d{0,11}$/.test(s) && parseVatNumber(raw)?.kind !== 'uk';
}

/** What HMRC holds for a VAT number. */
export interface VatProfile {
  /** The 9 or 12-digit VAT registration number. */
  vatNumber: string;
  /** The VAT-registered name. */
  name: string;
  /** Address lines as HMRC gives them, then postcode and country code. */
  address: { lines: string[]; postcode: string; countryCode: string };
}

/** What the Worker recorded for one VAT field at submit. */
export interface VatCheck {
  /** The number as checked (`GB123456789`) or as typed (non-UK). */
  number: string;
  checkedAt: string;
  /**
   * `verified`: HMRC holds it. `unavailable`: HMRC couldn't be reached.
   * `not_uk`: not a UK number, so HMRC can't check it.
   */
  outcome: 'verified' | 'unavailable' | 'not_uk';
  profile?: VatProfile;
  /** The Companies House name on the same application, when it differs. */
  companyNameDiffers?: string;
}

/** Reserved key in a submission's customer data holding the checks, by field id. */
export const VAT_CHECKS_KEY = '_hmrcVat';

export function vatChecksOf(data: Record<string, unknown>): Record<string, VatCheck> {
  const raw = data[VAT_CHECKS_KEY];
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, VatCheck>) : {};
}

/**
 * Whether two business names are the same once case, punctuation and the
 * usual suffixes (Ltd, Limited, PLC, LLP, & Co) are set aside.
 */
export function sameBusinessName(a: string, b: string): boolean {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9 ]+/g, ' ')
      .replace(/\b(the|limited|ltd|plc|llp|lp|company|co|uk)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  return norm(a) === norm(b);
}

/** One-line staff summary, e.g. "Registered to Bulrush Ltd (HMRC)". */
export function vatCheckSummary(check: VatCheck | undefined): string {
  if (!check) return 'Not checked with HMRC';
  if (check.outcome === 'not_uk') return 'Not a UK VAT number, so not checked with HMRC';
  if (check.outcome === 'unavailable' || !check.profile) {
    return 'NOT CHECKED: HMRC was unavailable when this was submitted, so check the VAT number by hand';
  }
  const base = `Registered to ${check.profile.name} (HMRC)`;
  return check.companyNameDiffers
    ? `${base}. CHECK: Companies House has the company as ${check.companyNameDiffers}`
    : base;
}

/** Whether staff should look at this check (not verified, or a name mismatch). */
export function vatCheckNeedsLook(check: VatCheck | undefined): boolean {
  return !check || check.outcome !== 'verified' || Boolean(check.companyNameDiffers);
}

/** The applicant-facing message for a UK number HMRC doesn't hold. */
export function vatNotFoundMessage(display: string): string {
  return `HMRC has no record of VAT number ${display}. Check it and try again.`;
}
